import React, { useEffect, useState } from "react";
import { Badge, Card, Col, Row, Stack } from "react-bootstrap";
import { User } from "../../types/user";
import { Tooltip } from "react-tooltip";
import { Member } from "../../types/member";
import useApp from "../../utils/useApp";

interface HouseholdState {
  user: User;
  selectedUser?: User | Member | undefined;
  setSelectedUser?: any;
  setUserType?: any;
  setShowEditFamily?: any;
  showDetails?: boolean;
}

const HouseholdCard = ({ user, selectedUser, setSelectedUser, setUserType, setShowEditFamily, showDetails = true }: HouseholdState) => {
  const { auth }: any = useApp();
  const [age, setAge] = useState<number | null>();
  const [banDateExpired, setBanDateExpired] = useState<boolean>(false);

  useEffect(() => {
    // Check if birthdate is valid
    if (user?.birthdate && user?.birthdate !== "0000-00-00") {
      const calculateAge = (birthdate: string) => {
        const birthDateObj = new Date(birthdate);
        const today = new Date();

        let age = today.getFullYear() - birthDateObj.getFullYear();
        const monthDiff = today.getMonth() - birthDateObj.getMonth();

        // Adjust age if the birthdate hasn't occurred yet this year
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDateObj.getDate())) {
          age--;
        }
        return age;
      };

      setAge(calculateAge(user.birthdate));
    } else {
      setAge(null); // Don't calculate age if birthdate is invalid
    }

    //check ban expiration date
    if (user.banned_expiration_at) {
      const expireDate = new Date(user?.banned_expiration_at);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setBanDateExpired(expireDate <= today);
    }
  }, [user]);

  const onClickEdit = () => {
    setSelectedUser(user);
    setUserType(user?.role === "family" ? "Guardian" : "Member");
    setShowEditFamily(true);
  };

  return (
    <Col key={user.user_id} xs={6} lg={4} className="pb-3">
      <Card
        key={user.user_id}
        className={`bg-light ${user?.is_banned ? "card-danger" : ""} card-household-edit-case  ${showDetails ? "cursor-pointer" : ""}`}
        onClick={showDetails ? () => onClickEdit() : undefined}
      >
        <Card.Body className="p-2">
          <Row className="me-0 mb-3">
            <Col xs={showDetails ? 8 : 12}>
              <Stack>
                <h6
                  className={selectedUser?.id === user.id ? "app-orange-text text-start pb-0 mb-0" : "app-header-text text-start pb-0 mb-0"}
                >
                  {user.firstname} {user.preferredname ? "'" + user.preferredname + "'" : ""} {user.lastname}
                </h6>
                {user.relationship === "child" ? (
                  <p className="text-muted text-start first-letter mb-0">
                    {user?.grade && age
                      ? `Grade ${user?.grade} - Age ${age}`
                      : user?.grade
                        ? `Grade ${user?.grade}`
                        : age
                          ? `Age ${age}`
                          : ""}
                  </p>
                ) : (
                  <p className="text-muted text-start first-letter mb-0">{user.relationship}</p>
                )}
              </Stack>
            </Col>
          </Row>
          {user.is_new && (
            <div className="position-absolute bottom-0 end-0">
              <Badge bg="none" className="app-tertiary-color me-2 mb-2">
                New
              </Badge>
            </div>
          )}
          {showDetails && (
            <Row>
              <Col xs={12}>
                <Stack direction="horizontal" gap={2} className="justify-content-end flex-wrap">
                  {user?.is_banned === 1 ? (
                    <>
                      {user?.is_case_subject === 1 && (
                        <span
                          className="role-dot role-dot-green"
                          title="Person Served"
                          data-tooltip-id={`tooltip-person-${user.id}`}
                          data-tooltip-content={`Person Served`}
                        >
                          <Tooltip id={`tooltip-person-${user.id}`} place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                        </span>
                      )}
                      {user?.is_residential === 1 && auth?.profile.includeHMIS === 1 && (
                        <span
                          className="role-dot role-dot-blue"
                          title="Residential"
                          data-tooltip-id={`tooltip-person-${user.id}`}
                          data-tooltip-content={`Residential`}
                        >
                          <Tooltip id={`tooltip-person-${user.id}`} place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                        </span>
                      )}
                      {user?.is_primary === 1 && (
                        <span
                          className="role-dot role-dot-orange"
                          title="Main Contact"
                          data-tooltip-id={`tooltip-contact-${user.id}`}
                          data-tooltip-content={`Main Contact`}
                        >
                          <Tooltip id={`tooltip-contact-${user.id}`} place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                        </span>
                      )}
                      <div>
                        <Badge bg="none" className="app-danger-color">
                          Banned
                        </Badge>
                        <small className="app-danger-text ms-2">
                          {banDateExpired
                            ? `Ban Expired`
                            : `Banned ${user?.banned_expiration_at ? "Until " + user?.banned_expiration_at : "Indefinitely"}`}
                        </small>
                      </div>
                    </>
                  ) : (
                    <>
                      {user?.is_case_subject === 1 && (
                        <Badge bg="none" className="app-secondary-color">
                          Person Served
                        </Badge>
                      )}
                      {user?.is_residential === 1 && (
                        <Badge bg="none" className="app-primary-color">
                          Residential
                        </Badge>
                      )}
                      {user?.is_primary === 1 && (
                        <Badge bg="none" className="app-tertiary-color">
                          Main Contact
                        </Badge>
                      )}
                    </>
                  )}
                </Stack>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>
    </Col>
  );
};

export default HouseholdCard;
